import React, { useEffect, useState } from "react";
import "./fundraiserAssets.scss";
import Loader from "../../common/Loader";
import { getMyAssets } from "../../../services/assetsService";
import { getS3PublicUrl } from "../../../utils/s3";

import APostersIcon from '../../../Assets/Images/Fundraiser/A-Posters.svg';
import DownloadIcon from '../../../Assets/Images/Fundraiser/Download-Icon.svg';
import EFlyersIcon from '../../../Assets/Images/Fundraiser/E-Flyers.svg';
import EmailTemplatesIcon from '../../../Assets/Images/Fundraiser/Email-Templates.svg';
import PostCardsIcon from '../../../Assets/Images/Fundraiser/Post-Cards.svg';
import QRCodesIcon from '../../../Assets/Images/Fundraiser/QR-Codes.svg';
import SocialMediaBannersIcon from '../../../Assets/Images/Fundraiser/Social-Media-Banners.svg';
import { PiFlagBannerLight, PiEyeLight, PiDownloadSimpleLight } from 'react-icons/pi';
import EmailSignatureIcon from '../../../Assets/Icons/emailsignature.svg';

const FundraiserAssets = () => {
  const [assetsList, setAssetsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setIsLoading(true);
      const response = await getMyAssets();
      if (response?.data?.success) {
        setAssetsList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const assetCategories = [
    { key: 'postcards', label: 'Postcards', icon: <img src={PostCardsIcon} alt="Postcards" className="w-9 h-9" /> },
    { key: 'a1Posters', label: 'A1 Posters', icon: <img src={APostersIcon} alt="A1 Posters" className="w-7 h-7" /> },
    { key: 'a2Posters', label: 'A2 Posters', icon: <img src={APostersIcon} alt="A2 Posters" className="w-7 h-7" /> },
    { key: 'a3Posters', label: 'A3 Posters', icon: <img src={APostersIcon} alt="A3 Posters" className="w-7 h-7" /> },
    { key: 'a4Posters', label: 'A4 Posters', icon: <img src={APostersIcon} alt="A4 Posters" className="w-7 h-7" /> },
    { key: 'socialMediaBanners', label: 'Social Media Banners', icon: <img src={SocialMediaBannersIcon} alt="Social Media Banners" className="w-9 h-9" /> },
    { key: 'digitalFlyer', label: 'Digital Flyers', icon: <img src={EFlyersIcon} alt="Digital Flyers" className="w-9 h-9" /> },
    { key: 'qrCodes', label: 'QR Codes', icon: <img src={QRCodesIcon} alt="QR Codes" className="w-7 h-7" /> },
    { key: 'emailTemplates', label: 'Email Templates', icon: <img src={EmailTemplatesIcon} alt="Email Templates" className="w-9 h-9" /> },
    { key: 'pullUpBanners', label: 'Pull up Banners', icon: <span style={{ color: '#00ADEF' }}><PiFlagBannerLight size={26} /></span> },
    { key: 'emailSignatures', label: 'Email Signatures', icon: <img src={EmailSignatureIcon} alt="Email Signatures" className="w-7 h-7" /> },
  ];

  const viewAsset = (url) => {
    if (!url) return;
    window.open(getS3PublicUrl(url), '_blank');
  };

  const downloadAsset = async (asset) => {
    const fileUrl = getS3PublicUrl(asset.file);
    if (!fileUrl) return;

    try {
      setIsDownloading(true);
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = asset.file.split('/').pop() || 'asset';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      window.open(fileUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  // Returns the best available timestamp for an asset (updatedAt, createdAt, or from MongoDB _id)
  const getAssetTime = (asset) => {
    const idTime = parseInt((asset._id || '').substring(0, 8), 16) * 1000 || 0;
    return Math.max(
      asset.updatedAt ? new Date(asset.updatedAt).getTime() : 0,
      asset.createdAt ? new Date(asset.createdAt).getTime() : 0,
      idTime
    );
  };

  const fieldMap = {
    postcardStandard: 'postcards',
    a1Poster: 'a1Posters',
    a2Poster: 'a2Posters',
    a3Poster: 'a3Posters',
    a4Poster: 'a4Posters',
    socialMediaBanner: 'socialMediaBanners',
    eFlyer: 'digitalFlyer',
    qrCode: 'qrCodes',
    emailTemplate: 'emailTemplates',
    pullUpBanner: 'pullUpBanners',
    emailSignature: 'emailSignatures',
  };

  // Pick the single newest enabled promotion and show only its assets
  const newestPromotion = [...(assetsList || [])]
    .filter(a => a.status !== false && a.status !== 'declined')
    .sort((a, b) => getAssetTime(b) - getAssetTime(a))[0] || null;

  const availableAssets = {};
  if (newestPromotion) {
    Object.entries(fieldMap).forEach(([field, categoryKey]) => {
      if (newestPromotion[field]) {
        availableAssets[categoryKey] = { ...newestPromotion, file: newestPromotion[field], category: categoryKey };
      }
    });
  }


  return (
    <>
      {isLoading && <Loader />}
      <div className="flex flex-col gap-3 md:gap-5">
        <h1 className="text-[18px] md:text-[22px] font-[600] text-[#303030] mt-4 mb-1 md:mb-2">Marketing Assets</h1>
      </div>
      <div className="bg-white rounded-xl shadow-md mt-4 md:mt-6 p-4 md:p-6 mb-8">
        <h2 className="text-sm md:text-base font-semibold text-[#303030] mb-4 md:mb-6">View and Download Artwork for Your Fundraiser Campaign</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {assetCategories.map((cat) => {
            const asset = availableAssets[cat.key];
            const isAvailable = !!asset;
            return (
              <div
                key={cat.key}
                className={`flex flex-row items-center justify-between border transition-all duration-200 rounded-lg w-full h-[60px] md:h-[47px] px-3 py-0 ${isAvailable
                  ? 'border-[#00ADEF] bg-[#F4FCFF] shadow-sm'
                  : 'border-[#E5E5E5] bg-white'
                  }`}
              >
                <div className="flex items-center gap-2 w-full overflow-hidden">
                  <span className="flex items-center justify-center w-8 flex-shrink-0">{cat.icon}</span>
                  <span className={`font-medium text-left break-words leading-tight text-[12px] truncate`}>
                    {cat.label}
                  </span>
                </div>
                {isAvailable && (
                  <div className="flex items-center gap-1 ml-1">
                    <button
                      className="flex items-center justify-center text-[#00ADEF] hover:scale-110 transition-transform p-1"
                      onClick={() => viewAsset(asset.file)}
                      title="View"
                    >
                      <PiEyeLight size={24} />
                    </button>
                    <button
                      className="flex items-center justify-center text-[#00ADEF] hover:scale-110 transition-transform p-1"
                      onClick={() => downloadAsset(asset)}
                      disabled={isDownloading}
                      title="Download"
                    >
                      <PiDownloadSimpleLight size={24} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default FundraiserAssets;
